import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Tag, Star, Zap, Phone, Gift, Sparkles } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

// Faixa promocional no topo — configurável no painel (Marketing > Faixa de Avisos).
// Modos: fixed (parado), rotate (alternadas), slide (deslizamento), marquee (contínua), pulse (pulsação).
// Tudo em CSS/transform; respeita prefers-reduced-motion (vira mensagem fixa).
export const ANNOUNCEMENT_ICONS = {
  none: null,
  truck: Truck,
  tag: Tag,
  star: Star,
  zap: Zap,
  phone: Phone,
  gift: Gift,
  sparkles: Sparkles,
};

export const AnnouncementBar = () => {
  const settings = useSettings();
  const cfg = settings.announcement || {};
  // mensagens ativas, fixadas primeiro
  const messages = (cfg.messages || [])
    .filter((m) => m.text && m.active !== false)
    .sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("in"); // in | out (modo slide)
  const reduceMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const animated = !reduceMotion && (settings.animations || {}).enabled !== false;
  const rotating = ["rotate", "slide"].includes(cfg.mode) && messages.length > 1 && animated;

  useEffect(() => {
    if (!rotating) return;
    const t = setInterval(() => {
      if (cfg.mode === "slide") {
        setPhase("out");
        setTimeout(() => {
          setIdx((i) => (i + 1) % messages.length);
          setPhase("in");
        }, 280);
      } else {
        setIdx((i) => (i + 1) % messages.length);
      }
    }, Math.max(2, cfg.speed_seconds || 5) * 1000);
    return () => clearInterval(t);
  }, [rotating, messages.length, cfg.speed_seconds, cfg.mode]);

  if (!cfg.enabled || messages.length === 0) return null;

  const style = { backgroundColor: cfg.bg_color || "#07335C", color: cfg.text_color || "#ffffff" };
  const marquee = cfg.mode === "marquee" && animated;
  const pulse = cfg.mode === "pulse" && animated;

  const renderMsg = (m, key) => {
    const Icon = ANNOUNCEMENT_ICONS[m.icon] || null;
    const content = (
      <span className="inline-flex items-center gap-1.5 px-8 whitespace-nowrap">
        {Icon && <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />}
        {m.text}
      </span>
    );
    return m.link ? (
      <Link key={key} to={m.link} className="hover:underline" data-testid={`announcement-link-${key}`}>{content}</Link>
    ) : (
      <span key={key}>{content}</span>
    );
  };

  const current = messages[idx % messages.length];

  return (
    <div
      className="text-xs sm:text-sm font-semibold py-2 overflow-hidden relative"
      style={style}
      data-testid="announcement-bar"
      data-mode={cfg.mode || "fixed"}
      role="status"
    >
      {marquee ? (
        // 3 cópias + deslocamento de 1/3 = loop contínuo sem corte (direita → esquerda)
        <div className="flex whitespace-nowrap announcement-marquee" style={{ animationDuration: `${Math.max(10, (cfg.speed_seconds || 5) * 4)}s` }} data-testid="announcement-marquee">
          {[...messages, ...messages, ...messages].map((m, i) => renderMsg(m, i))}
        </div>
      ) : cfg.mode === "slide" && animated ? (
        <div className="text-center container-store">
          <div
            key={idx}
            className={phase === "in" ? "announcement-slide" : ""}
            style={phase === "out" ? { animation: "announcement-slide-out 0.28s ease both" } : undefined}
            data-testid="announcement-slide"
          >
            {renderMsg(current, "current")}
          </div>
        </div>
      ) : (
        <div className={`text-center container-store transition-opacity duration-300 ${pulse ? "animate-pulse" : ""}`} data-testid="announcement-static">
          {renderMsg(current, "current")}
        </div>
      )}
    </div>
  );
};
